import React, { useCallback, useState } from "react";

import { faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { graphql } from "relay-runtime";

import AgendaGeneratorPreview from "~/agendaGenerator/AgendaGeneratorPreview";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button, useModals } from "~/ui";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import toasty from "~/ui/toast";

import type { agendaGeneratorDismissAgendaGeneratorMutation } from "~/__generated__/agendaGeneratorDismissAgendaGeneratorMutation.graphql";
import type { NoteCard_note$data as Note } from "~/__generated__/NoteCard_note.graphql";
import type { NoteCard_viewer$data as Viewer } from "~/__generated__/NoteCard_viewer.graphql";

import styles from "./AgendaGenerator.module.css";

interface AgendaGeneratorProps {
	note: Note;
	viewer: Viewer;
}

const dismissAgendaGeneratorMutation = graphql`
	mutation agendaGeneratorDismissAgendaGeneratorMutation($noteId: ID!) {
		dismissAgendaGenerator(noteId: $noteId) {
			note {
				viewerAgendaGeneratorBannerDismissed
			}
		}
	}
`;

export default function AgendaGenerator({ note, viewer }: AgendaGeneratorProps) {
	const [loading, setLoading] = useState<boolean>(false);
	const modals = useModals();

	const onPreview = useCallback(() => {
		modals.open(({ close }) => <AgendaGeneratorPreview close={close} note={note} viewer={viewer} />);
	}, [modals, note, viewer]);

	const onDismiss = useCallback(() => {
		setLoading(true);
		commitMutationPromise<agendaGeneratorDismissAgendaGeneratorMutation>(environment, {
			mutation: dismissAgendaGeneratorMutation,
			variables: {
				noteId: note.id,
			},
			onCompleted: () => {
				setLoading(false);
			},
			onError: () => {
				setLoading(false);
				toasty.error("Error while dismissing the banner. Please try again.");
			},
		});
	}, [note]);

	return (
		<article className={styles.wrapper}>
			<FontAwesomeIcon icon={faSparkles} className={styles.sparkles} />
			Generate agenda from event description?
			<div className={styles.actions}>
				<Button size="slim" variant="primary" onClick={onPreview} disabled={loading}>
					{loading && <LoadingSpinner inline size="xs" />} Preview
				</Button>
				<Button size="slim" variant="secondary" onClick={onDismiss} disabled={loading}>
					{loading && <LoadingSpinner inline size="xs" />} Dismiss
				</Button>
			</div>
		</article>
	);
}
